<script lang="ts">
    import { onMount } from 'svelte'
    import type { GoalboardDataDto, GoalboardMemberDto } from './goalboard-member.dto'
    import dayjs from 'dayjs'

    export let showName: boolean = true
    export let data: GoalboardDataDto

    let pointPosition = null

    function getSize(max: number, current: number) {
        if (max === 0) return 0
        let w = Math.round((100 / max) * current)
        if (w > 100) w = 100
        return w.toString()
    }

    function getTeamSize() {
        if (data.goal.goalType === 'individual') return 1
        return data.goal.users.length
    }

    let periodTotal = 0
    let periodCurrent = 0
    function evaluatePointPosition() {
        // identifica o tamanho do período e o momento atual (para exibir um parcial da meta)
        const startDate = new Date(data.dates.from)
        const endDate = new Date(data.dates.to)
        startDate.setMinutes(startDate.getMinutes() - window.store.company.timezone)
        endDate.setMinutes(endDate.getMinutes() - window.store.company.timezone)
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        while (startDate.getTime() < endDate.getTime()) {
            if (data.goal.workingDays.includes(startDate.getDay())) {
                periodTotal += 1
                let diff = dayjs(now).diff(startDate, 'day')
                if (diff >= 0) {
                    periodCurrent += 1
                }
            }
            startDate.setDate(startDate.getDate() + 1)
        }

        if (periodTotal === 2) pointPosition = null
        pointPosition = Math.round((periodCurrent / periodTotal) * 100)
        if (pointPosition >= 100) pointPosition = null
    }

    function getColor(max: number, current: number) {
        let result = Math.round((100 / max) * Math.round(periodCurrent / periodTotal) * current)
        if (result > 100) result = 100
        if (result >= 95) return 'user_line_goal_success'
        if (result < 50) return 'user_line_goal_danger'
        return 'user_line_goal_alert'
    }

    function round(value: number) {
        return Math.ceil(value).toString()
    }

    let loaded = false
    onMount(() => {
        evaluatePointPosition()
        loaded = true
    })
</script>

{#if loaded}
    <div class="user_box config-box">
        {#if showName}
            <div class="user_name config-box-title">{data.member.user?.userName}</div>
            <div class="meta_name">Meta: {data.goal.label}</div>
        {:else}
            <div class="user_name config-box-title">Meta: {data.goal.label}</div>
        {/if}
        {#if data.goal.contactedGoal > 0}
            <div class="user_line">
                <div class="user_line_data">
                    <div>{data.member.contacted} contactados</div>
                    <div class="spacer"></div>
                    Meta {round(data.goal.contactedGoal / getTeamSize())}
                </div>
                <div class="user_line_bar">
                    <div
                        class="user_line_bar_in {getColor(
                            data.goal.contactedGoal / getTeamSize(),
                            data.member.contacted,
                        )}"
                        style="width:{getSize(data.goal.contactedGoal / getTeamSize(), data.member.contacted)}%;"
                    ></div>
                    {#if pointPosition}
                        <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                    {/if}
                </div>
            </div>
        {/if}
        {#if data.goal.connectedGoal > 0}
            <div class="user_line">
                <div class="user_line_data">
                    <div>{data.member.connected} responderam</div>
                    <div class="spacer"></div>
                    Meta {round(data.goal.connectedGoal / getTeamSize())}
                </div>
                <div class="user_line_bar">
                    <div
                        class="user_line_bar_in {getColor(
                            data.goal.connectedGoal / getTeamSize(),
                            data.member.connected,
                        )}"
                        style="width:{getSize(data.goal.connectedGoal / getTeamSize(), data.member.connected)}%;"
                    ></div>
                    {#if pointPosition}
                        <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                    {/if}
                </div>
            </div>
        {/if}
        {#if data.goal.scheduledGoal > 0}
            <div class="user_line">
                <div class="user_line_data">
                    <div>{data.member.scheduled} agendamentos</div>
                    <div class="spacer"></div>
                    Meta {round(data.goal.scheduledGoal / getTeamSize())}
                </div>
                <div class="user_line_bar">
                    <div
                        class="user_line_bar_in {getColor(
                            data.goal.scheduledGoal / getTeamSize(),
                            data.member.scheduled,
                        )}"
                        style="width:{getSize(data.goal.scheduledGoal / getTeamSize(), data.member.scheduled)}%;"
                    ></div>
                    {#if pointPosition}
                        <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                    {/if}
                </div>
            </div>
        {/if}

        {#if data.goal.resultType === 'relative'}
            {#if data.goal.showGoal > 0}
                <div class="user_line">
                    <div class="user_line_data">
                        <div>{data.member.showFromMinePercentual}% ({data.member.showFromMine}) comparecimentos</div>
                        <div class="spacer"></div>
                        Meta {data.goal.showGoal}%
                    </div>
                    <div class="user_line_bar">
                        <div
                            class="user_line_bar_in {getColor(data.goal.showGoal, data.member.showFromMinePercentual)}"
                            style="width:{getSize(data.goal.showGoal, data.member.showFromMinePercentual)}%;"
                        ></div>
                        {#if pointPosition}
                            <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                        {/if}
                    </div>
                </div>
            {/if}
            {#if data.goal.conversionGoal > 0}
                <div class="user_line">
                    <div class="user_line_data">
                        <div>
                            {data.member.convertedFromMinePercentual}% ({data.member.convertedFromMine}) dos meus
                            agendamentos converteram
                        </div>
                        <div class="spacer"></div>
                        Meta {data.goal.conversionGoal}%
                    </div>
                    <div class="user_line_bar">
                        <div
                            class="user_line_bar_in {getColor(
                                data.goal.conversionGoal,
                                data.member.convertedFromMinePercentual,
                            )}"
                            style="width:{getSize(data.goal.conversionGoal, data.member.convertedFromMinePercentual)}%;"
                        ></div>
                        {#if pointPosition}
                            <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                        {/if}
                    </div>
                </div>
                <div class="user_line">
                    <div class="user_line_data">
                        <div>
                            {data.member.convertedByMePercentual}% ({data.member.convertedByMe}) dos meus atendimentos
                            converteram
                        </div>
                        <div class="spacer"></div>
                        Meta {data.goal.conversionGoal}%
                    </div>
                    <div class="user_line_bar">
                        <div
                            class="user_line_bar_in {getColor(
                                data.goal.conversionGoal,
                                data.member.convertedByMePercentual,
                            )}"
                            style="width:{getSize(data.goal.conversionGoal, data.member.convertedByMePercentual)}%;"
                        ></div>
                        {#if pointPosition}
                            <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                        {/if}
                    </div>
                </div>
            {/if}
        {:else}
            {#if data.goal.showGoal > 0}
                <div class="user_line">
                    <div class="user_line_data">
                        <div>{data.member.showFromMine} comparecimentos</div>
                        <div class="spacer"></div>
                        Meta {round(data.goal.showGoal / getTeamSize())}
                    </div>
                    <div class="user_line_bar">
                        <div
                            class="user_line_bar_in {getColor(
                                data.goal.showGoal / getTeamSize(),
                                data.member.showFromMine,
                            )}"
                            style="width:{getSize(data.goal.showGoal / getTeamSize(), data.member.showFromMine)}%;"
                        ></div>
                        {#if pointPosition}
                            <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                        {/if}
                    </div>
                </div>
            {/if}
            {#if data.goal.conversionGoal > 0}
                <div class="user_line">
                    <div class="user_line_data">
                        <div>{data.member.convertedFromMine} dos meus agendamentos converteram</div>
                        <div class="spacer"></div>
                        Meta {round(data.goal.conversionGoal / getTeamSize())}
                    </div>
                    <div class="user_line_bar">
                        <div
                            class="user_line_bar_in {getColor(
                                data.goal.conversionGoal / getTeamSize(),
                                data.member.convertedFromMine,
                            )}"
                            style="width:{getSize(
                                data.goal.conversionGoal / getTeamSize(),
                                data.member.convertedFromMine,
                            )}%;"
                        ></div>
                        {#if pointPosition}
                            <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                        {/if}
                    </div>
                </div>
                <div class="user_line">
                    <div class="user_line_data">
                        <div>{data.member.convertedByMe} dos meus atendimentos converteram</div>
                        <div class="spacer"></div>
                        Meta {round(data.goal.conversionGoal / getTeamSize())}
                    </div>
                    <div class="user_line_bar">
                        <div
                            class="user_line_bar_in {getColor(
                                data.goal.conversionGoal / getTeamSize(),
                                data.member.convertedByMe,
                            )}"
                            style="width:{getSize(
                                data.goal.conversionGoal / getTeamSize(),
                                data.member.convertedByMe,
                            )}%;"
                        ></div>
                        {#if pointPosition}
                            <div class="user_line_ball" style="margin-left:{pointPosition}%;"></div>
                        {/if}
                    </div>
                </div>
            {/if}
        {/if}
    </div>
{/if}

<style>
    .user_box {
        flex-grow: 1;
        flex: 0 0 436px;
    }

    .user_box .user_line {
        margin-bottom: 16px;
    }

    .meta_name {
        margin-top: -12px;
        margin-bottom: 8px;
        opacity: 0.7;
    }

    .user_line_data {
        display: flex;
        font-size: 14px;
        opacity: 0.55;
    }

    .user_line_bar {
        background-color: #99999920;
        border-radius: 4px;
    }
    .user_line_bar_in {
        min-height: 8px;
        background: linear-gradient(#99999999, #999999);
        font-size: 12px;
        overflow: hidden;
        text-wrap: nowrap;
        border-radius: 4px;
    }

    .user_line_goal_alert {
        background: linear-gradient(#ffc10799, #ffc107) !important;
    }

    .user_line_goal_danger {
        background: linear-gradient(#dc354599, #dc3545) !important;
    }

    .user_line_goal_success {
        background: linear-gradient(#5dcb9899, #5dcb98) !important;
    }

    .user_line_ball {
        background-color: #66666699;
        border-radius: 4px;
        width: 4px;
        height: 8px;

        position: absolute;
        z-index: 1;
        top: 0px;
    }

    .user_line_bar {
        position: relative;
    }
</style>
